import React, { useEffect } from "react";
import { ChevronDown, ChevronUp, Plus, Loader, Pencil } from "tabler-icons-react";
import EnfantAvatar from "./EnfantAvatar";

/**
 * Carte "enfant courant" posée sur le bandeau coloré.
 * Un tap ouvre la feuille de sélection : plus besoin d'une page dédiée.
 */
export function EnfantSelectorCard({ eleve, opened, onToggle, isLoading }) {
  const fullName =
    eleve?.fullName ??
    [eleve?.firstName, eleve?.lastName].filter(Boolean).join(" ");

  return (
    <button
      onClick={onToggle}
      aria-expanded={opened}
      className="w-full bg-white rounded-2xl px-3 py-2.5 flex items-center gap-3 shadow-[0_10px_25px_-12px_rgba(0,0,0,0.45)] active:scale-[0.995] transition-transform"
    >
      <EnfantAvatar id={eleve?.id} name={fullName} size={44} />
      <div className="flex-1 min-w-0 text-left">
        {isLoading && !fullName ? (
          <>
            <span className="block h-3.5 w-28 bg-black/10 rounded animate-pulse mb-1.5" />
            <span className="block h-2.5 w-12 bg-black/10 rounded animate-pulse" />
          </>
        ) : (
          <>
            <p className="font-bold text-primary text-[16px] leading-tight truncate">
              {fullName || "Mon enfant"}
            </p>
            <p className="text-[13px] text-primary/50 leading-tight truncate">
              {eleve?.classe?.name ?? eleve?.classe ?? "—"}
            </p>
          </>
        )}
      </div>
      <span className="text-primary/60 pr-1">
        {opened ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
      </span>
    </button>
  );
}

/**
 * Feuille de sélection d'enfant (bottom sheet).
 */
export function EnfantSheet({
  opened,
  onClose,
  eleves = [],
  currentId,
  onSelect,
  onAdd,
  onEdit,
  isLoading,
}) {
  useEffect(() => {
    if (!opened) return;
    const onKey = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened, onClose]);

  return (
    <>
      {/* Fond */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[55] bg-black/50 transition-opacity duration-200 ${
          opened ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Feuille */}
      <div
        role="dialog"
        aria-label="Choisir un enfant"
        className={`fixed z-[60] bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[500px] bg-white rounded-t-[28px] transition-transform duration-300 ease-out ${
          opened ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="pt-3 pb-2 flex justify-center">
          <span className="h-1.5 w-12 rounded-full bg-black/15" />
        </div>

        <div className="max-h-[60dvh] overflow-y-auto px-5 pb-2">
          {isLoading ? (
            <div className="py-10 flex justify-center">
              <Loader size={26} className="animate-spin text-secondary" />
            </div>
          ) : (
            eleves.map((eleve, index) => {
              const active = String(eleve?.id) === String(currentId);
              return (
                <div
                  key={eleve?.id ?? index}
                  className={`w-full flex items-center gap-2 ${
                    index === 0 ? "" : "border-t border-black/[0.06]"
                  }`}
                >
                  {/* Sélection de l'enfant */}
                  <button
                    onClick={() => onSelect?.(eleve)}
                    className="flex-1 min-w-0 flex items-center gap-3 py-4 text-left"
                  >
                    <EnfantAvatar
                      id={eleve?.id}
                      name={[eleve?.firstName, eleve?.lastName]
                        .filter(Boolean)
                        .join(" ")}
                      size={44}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-primary text-[16px] leading-tight truncate">
                        {[eleve?.firstName, eleve?.lastName]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                      <p className="text-[13px] text-primary/50 leading-tight truncate">
                        {eleve?.classe?.name}
                      </p>
                    </div>
                  </button>

                  {/* Modification du profil (prénom, nom, classe...) */}
                  <button
                    aria-label={`Modifier le profil de ${eleve?.firstName ?? "l'enfant"}`}
                    onClick={() => onEdit?.(eleve)}
                    className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full text-primary/40 active:bg-black/[0.06] transition-colors"
                  >
                    <Pencil size={19} />
                  </button>

                  <button
                    aria-label={`Choisir ${eleve?.firstName ?? "cet enfant"}`}
                    onClick={() => onSelect?.(eleve)}
                    className="h-10 w-8 shrink-0 flex items-center justify-center"
                  >
                    <span
                      className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                        active
                          ? "border-secondary bg-secondary"
                          : "border-black/20"
                      }`}
                    >
                      {active ? (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      ) : null}
                    </span>
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="px-5 pt-2 pb-[calc(20px+env(safe-area-inset-bottom))] border-t border-black/[0.06]">
          <button
            onClick={onAdd}
            className="w-full h-12 rounded-2xl border-2 border-dashed border-secondary/40 text-secondary font-bold flex items-center justify-center gap-2 active:bg-secondary/5"
          >
            <Plus size={18} />
            Ajouter un enfant
          </button>
        </div>
      </div>
    </>
  );
}

export default EnfantSheet;
